import { EventEmitter } from '../utils/event-emitter.js';
import {
  querySelector,
  animateElement,
  throttle,
  getScrollPosition,
  isMobile,
} from '../utils/helpers.js';
import {
  SELECTORS,
  CSS_CLASSES,
  EVENTS,
  ANIMATION_CONFIG,
  MESSAGES,
} from '../utils/constants.js';

/**
 * Менеджер пользовательского интерфейса
 * Отвечает за общие UI взаимодействия, анимации и обратную связь
 */
export class UIManager extends EventEmitter {
  constructor() {
    super();
    this.scrollToTopButton = null;
    this.loadingOverlay = null;
    this.notificationContainer = null;

    this.isScrolling = false;
    this.lastScrollPosition = 0;

    this.throttledScrollHandler = null;
    this.eventsInitialized = false;
  }

  /**
   * Инициализация менеджера
   */
  init() {
    this.scrollToTopButton = querySelector(SELECTORS.SCROLL_TO_TOP_BTN);

    if (!this.scrollToTopButton) {
      console.warn('Кнопка "наверх" не найдена');
    }

    this.createNotificationContainer();
    this.setupEventListeners();

    console.log('🎨 UIManager инициализирован');
  }

  /**
   * Создание контейнера для уведомлений
   */
  createNotificationContainer() {
    this.notificationContainer = document.createElement('div');
    this.notificationContainer.className = 'notification-container';
    this.notificationContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    `;
    document.body.appendChild(this.notificationContainer);
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    if (this.eventsInitialized) return;

    // Throttled обработчик скролла для производительности
    this.throttledScrollHandler = throttle(
      this.handleScroll.bind(this),
      100,
    );

    // Обработчик скролла
    window.addEventListener('scroll', this.throttledScrollHandler, { passive: true });

    // Кнопка "наверх"
    this.scrollToTopButton?.addEventListener('click', () => {
      this.scrollToTop();
    });

    // Обработчик изменения размера окна
    window.addEventListener('resize', throttle(() => {
      this.handleResize();
    }, 250));

    // Обработчик focus/blur для accessibility
    window.addEventListener('focus', () => {
      this.handleWindowFocus();
    });

    window.addEventListener('blur', () => {
      this.handleWindowBlur();
    });

    this.eventsInitialized = true;
  }

  /**
   * Обработка скролла страницы
   */
  handleScroll() {
    const currentPosition = getScrollPosition();
    const isScrollingDown = currentPosition > this.lastScrollPosition;

    // Показ/скрытие кнопки "наверх"
    this.updateScrollToTopButton(currentPosition);

    // Обновляем состояние прокрутки
    this.isScrolling = true;
    this.lastScrollPosition = currentPosition;

    // Эмитируем событие скролла
    this.emit('scroll', {
      position: currentPosition,
      direction: isScrollingDown ? 'down' : 'up',
      isScrolling: this.isScrolling,
    });

    // Сбрасываем флаг скроллинга через некоторое время
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);
  }

  /**
   * Обновление видимости кнопки "наверх"
   * @param {number} scrollPosition - Текущая позиция скролла
   */
  updateScrollToTopButton(scrollPosition) {
    if (!this.scrollToTopButton) return;

    const shouldShow = scrollPosition > ANIMATION_CONFIG.SCROLL_THRESHOLD;

    if (shouldShow) {
      this.scrollToTopButton.classList.add(CSS_CLASSES.SHOW);
    } else {
      this.scrollToTopButton.classList.remove(CSS_CLASSES.SHOW);
    }
  }

  /**
   * Плавная прокрутка наверх
   */
  scrollToTop() {
    // Анимация нажатия кнопки
    if (this.scrollToTopButton) {
      this.scrollToTopButton.style.transform = 'translateY(-3px) scale(0.95)';
      setTimeout(() => {
        this.scrollToTopButton.style.transform = '';
      }, 150);
    }

    // Плавная прокрутка
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    // Эмитируем событие
    this.emit(EVENTS.SCROLL_TO_TOP);
  }

  /**
   * Обработка изменения размера окна
   */
  handleResize() {
    // Обновляем мобильное состояние
    const isMobileNow = isMobile();

    // Эмитируем событие изменения размера
    this.emit('resize', {
      isMobile: isMobileNow,
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  /**
   * Обработка получения фокуса окном
   */
  handleWindowFocus() {
    this.emit('windowFocus');
  }

  /**
   * Обработка потери фокуса окном
   */
  handleWindowBlur() {
    this.emit('windowBlur');
  }

  /**
   * Показ состояния загрузки
   * @param {boolean} show - Показать или скрыть
   * @param {string} message - Сообщение загрузки
   */
  showLoading(show = true, message = MESSAGES.LOADING) {
    if (show) {
      if (!this.loadingOverlay) {
        this.createLoadingOverlay();
      }
      this.loadingOverlay.querySelector('.loading-message').textContent = message;
      this.loadingOverlay.classList.add(CSS_CLASSES.SHOW);
      document.body.classList.add(CSS_CLASSES.LOADING);
    } else {
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.remove(CSS_CLASSES.SHOW);
      }
      document.body.classList.remove(CSS_CLASSES.LOADING);
    }
  }

  /**
   * Создание overlay для состояния загрузки
   */
  createLoadingOverlay() {
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.className = 'loading-overlay';
    this.loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    `;

    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner';
    spinner.style.cssText = `
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #007a63;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 15px;
    `;

    const message = document.createElement('div');
    message.className = 'loading-message';
    message.style.cssText = `
      font-size: 16px;
      color: #007a63;
      font-weight: 500;
    `;
    message.textContent = MESSAGES.LOADING;

    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
      background: white;
      padding: 20px 30px;
      border-radius: 10px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    `;

    container.appendChild(spinner);
    container.appendChild(message);
    this.loadingOverlay.appendChild(container);

    // Добавляем CSS анимацию
    if (!document.getElementById('loading-styles')) {
      const style = document.createElement('style');
      style.id = 'loading-styles';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading-overlay.show {
          opacity: 1 !important;
          visibility: visible !important;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(this.loadingOverlay);
  }

  /**
   * Показ уведомления
   * @param {string} message - Текст уведомления
   * @param {string} type - Тип уведомления (success, error, warning, info)
   * @param {number} duration - Продолжительность показа в мс
   */
  showNotification(message, type = 'info', duration = 4000) {
    const notification = this.createNotification(message, type);
    this.notificationContainer.appendChild(notification);

    // Анимация появления
    animateElement(notification, CSS_CLASSES.SLIDE_UP, 0);

    // Автоматическое скрытие
    if (duration > 0) {
      setTimeout(() => {
        this.hideNotification(notification);
      }, duration);
    }

    return notification;
  }

  /**
   * Создание элемента уведомления
   * @param {string} message - Текст уведомления
   * @param {string} type - Тип уведомления
   * @returns {Element} - DOM элемент уведомления
   */
  createNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;

    const colors = {
      success: '#27ae60',
      error: '#e74c3c',
      warning: '#f39c12',
      info: '#3498db',
    };

    notification.style.cssText = `
      background: ${colors[type] || colors.info};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-size: 14px;
      font-weight: 500;
      max-width: 350px;
      word-wrap: break-word;
      transform: translateX(100%);
      opacity: 0;
      transition: all 0.3s ease;
      pointer-events: auto;
      cursor: pointer;
      position: relative;
      padding-right: 40px;
    `;

    // Текст уведомления
    const text = document.createElement('span');
    text.textContent = message;
    notification.appendChild(text);

    // Кнопка закрытия
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '×';
    closeButton.style.cssText = `
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: white;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hideNotification(notification);
    });

    notification.appendChild(closeButton);

    // Клик по уведомлению для скрытия
    notification.addEventListener('click', () => {
      this.hideNotification(notification);
    });

    return notification;
  }

  /**
   * Скрытие уведомления
   * @param {Element} notification - Элемент уведомления
   */
  hideNotification(notification) {
    notification.style.transform = 'translateX(100%)';
    notification.style.opacity = '0';

    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
  }

  /**
   * Очистка всех уведомлений
   */
  clearAllNotifications() {
    const notifications = this.notificationContainer.querySelectorAll('.notification');
    notifications.forEach(notification => {
      this.hideNotification(notification);
    });
  }

  /**
   * Обновление счетчика результатов
   * @param {number} count - Количество результатов
   */
  updateMovieCount(count) {
    // Можно добавить отображение счетчика в UI
    this.emit('movieCountUpdated', count);
  }

  /**
   * Переключение видимости элемента "Нет результатов"
   * @param {boolean} show - Показать или скрыть
   */
  toggleNoResults(show) {
    const noResultsElement = querySelector(SELECTORS.NO_RESULTS);
    if (!noResultsElement) return;

    if (show) {
      noResultsElement.classList.remove(CSS_CLASSES.HIDDEN);
      animateElement(noResultsElement, CSS_CLASSES.FADE_IN, 0);
    } else {
      noResultsElement.classList.add(CSS_CLASSES.HIDDEN);
    }
  }

  /**
   * Анимация элементов списка
   * @param {NodeList} elements - Элементы для анимации
   * @param {string} animationClass - CSS класс анимации
   * @param {number} staggerDelay - Задержка между элементами
   */
  animateListItems(elements, animationClass = CSS_CLASSES.FADE_IN, staggerDelay = ANIMATION_CONFIG.STAGGER_DELAY) {
    elements.forEach((element, index) => {
      animateElement(element, animationClass, index * staggerDelay);
    });
  }

  /**
   * Показ сообщения об ошибке
   * @param {string} message - Текст ошибки
   * @param {Error} error - Объект ошибки (опционально)
   */
  showError(message, error = null) {
    console.error('UI Error:', message, error);
    this.showNotification(message, 'error', 6000);
  }

  /**
   * Показ сообщения об успехе
   * @param {string} message - Текст сообщения
   */
  showSuccess(message) {
    this.showNotification(message, 'success', 3000);
  }

  /**
   * Получение статистики UI
   * @returns {Object} - Статистика
   */
  getStatistics() {
    return {
      isScrolling: this.isScrolling,
      lastScrollPosition: this.lastScrollPosition,
      notificationCount: this.notificationContainer?.children.length || 0,
      isMobile: isMobile(),
      scrollToTopVisible: this.scrollToTopButton?.classList.contains(CSS_CLASSES.SHOW) || false,
    };
  }

  /**
   * Деструктор
   */
  destroy() {
    // Убираем обработчики событий
    if (this.throttledScrollHandler) {
      window.removeEventListener('scroll', this.throttledScrollHandler);
    }

    // Очищаем таймауты
    clearTimeout(this.scrollTimeout);

    // Удаляем созданные элементы
    if (this.loadingOverlay && this.loadingOverlay.parentNode) {
      this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
    }

    if (this.notificationContainer && this.notificationContainer.parentNode) {
      this.notificationContainer.parentNode.removeChild(this.notificationContainer);
    }

    this.removeAllListeners();
    this.scrollToTopButton = null;
    this.loadingOverlay = null;
    this.notificationContainer = null;
  }
}
